/**
 * Lottery footer: sponsor strip (§7.1 FOOTER_STRIP / SPONSOR_STRIP, labelled "Sponsored"), rules / terms /
 * privacy / support links, organizer line with the terms version.
 */
import { Link } from 'react-router';
import type { PublicLotteryDto } from '@windrop/contracts';
import { SponsorStrip, useIntl } from '@windrop/ui';
import { track } from '../lib/analytics';
import { sponsorRef } from './LotteryHeader';

export function LotteryFooter({ lottery }: { lottery: PublicLotteryDto }) {
  const { t } = useIntl();
  const strip = lottery.sponsors
    .filter((s) => s.placements.includes('FOOTER_STRIP') || s.placements.includes('SPONSOR_STRIP'))
    .sort((a, b) => b.displayWeight - a.displayWeight)
    .map(sponsorRef);
  return (
    <footer className="lottery-footer">
      {strip.length ? (
        <SponsorStrip
          sponsors={strip}
          label={t('shell.sponsored', {}, 'Sponsored')}
          placementId="FOOTER_STRIP"
          onImpression={(p) =>
            track(
              'sponsor_impression',
              { sponsorId: p.sponsorId, placement: 'FOOTER_STRIP', viewable: true, click: false },
              { placement: 'FOOTER_STRIP' },
            )
          }
          onClick={(p) =>
            track(
              'sponsor_impression',
              { sponsorId: p.sponsorId, placement: 'FOOTER_STRIP', viewable: true, click: true },
              { placement: 'FOOTER_STRIP' },
            )
          }
        />
      ) : null}
      <nav className="lottery-footer__links" aria-label={t('shell.rules', {}, 'Rules')}>
        {lottery.rulesUrl ? <a href={lottery.rulesUrl}>{t('shell.terms', {}, 'Full terms')}</a> : null}
        {lottery.privacyUrl ? <a href={lottery.privacyUrl}>{t('shell.privacy', {}, 'Privacy')}</a> : null}
        <Link to={supportLink(lottery)}>{t('shell.support', {}, 'Help and support')}</Link>
        <Link to="/preferences">{t('shell.preferences', {}, 'Preferences')}</Link>
      </nav>
      <p className="wd-muted lottery-footer__line">
        {t('shell.footer.rights', { organizer: lottery.organizer.name, terms: lottery.termsVersion })}
      </p>
    </footer>
  );
}

/** The lottery's support route: an in-app path or an https URL (config `identity.supportRoute`). */
export function supportLink(lottery: Pick<PublicLotteryDto, 'supportRoute' | 'slug'>): string {
  const r = lottery.supportRoute || '/support';
  if (/^https?:/i.test(r)) return r;
  const sep = r.includes('?') ? '&' : '?';
  return `${r}${sep}lottery=${encodeURIComponent(lottery.slug)}`;
}
